import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Checkbox, Form, InputNumber, Select, Space, Table, Tag, Typography, message } from 'antd';
import { SmartBreadcrumbs } from '@/components/Breadcrumbs';
import { useAuthStore } from '@/store/authStore';
import {
  fetchAiAgentConfig,
  fetchHowToUsage,
  saveAiAgentConfig,
  type AiAgentConfig,
  type HowToUsageSummary,
} from '@/services/aiAgentConfig.service';
import { formatDateTime } from '@/utils/format';
import { fetchRagMetrics } from '@/services/aiAgent.service';

const { Title, Text } = Typography;

const MODULE_OPTIONS = [
  'company',
  'sales',
  'procurement',
  'manufacturing',
  'finance',
  'quality',
  'projects',
  'hr',
];

const TOOL_OPTIONS = [
  'queryERPData',
  'searchDocuments',
  'summarizeDocument',
  'openPage',
  'openFilteredList',
  'getOverdueItems',
  'createDraftFromNaturalLanguage',
  'getHowToGuide',
  'searchHowToGuides',
];

type FormVals = {
  enabled: boolean;
  chartsEnabled: boolean;
  exportEnabled: boolean;
  maxRows: number;
  allowedModules: string[];
  allowedTools: string[];
};

export const AiAgentConfigPage: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const [form] = Form.useForm<FormVals>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [companyId, setCompanyId] = useState('');
  const [ragMetrics, setRagMetrics] = useState<Awaited<ReturnType<typeof fetchRagMetrics>> | null>(null);
  const [howTo, setHowTo] = useState<HowToUsageSummary | null>(null);

  const load = useCallback(async () => {
    if (!user?.is_super_admin) return;
    setLoading(true);
    try {
      const res = await fetchAiAgentConfig();
      if (!res.success) {
        message.error('Failed to load AI assistant config');
        return;
      }
      setCompanyId(res.companyId);
      const cfg = res.config;
      form.setFieldsValue({
        enabled: cfg.enabled,
        chartsEnabled: cfg.chartsEnabled,
        exportEnabled: cfg.exportEnabled,
        maxRows: cfg.maxRows,
        allowedModules: cfg.allowedModules || [],
        allowedTools: cfg.allowedTools || [],
      });
      try {
        const m = await fetchRagMetrics(7);
        setRagMetrics(m);
      } catch {
        setRagMetrics(null);
      }
      try {
        setHowTo(await fetchHowToUsage(30));
      } catch {
        setHowTo(null);
      }
    } catch (e) {
      const m = e instanceof Error ? e.message : 'Failed to load config';
      message.error(m);
    } finally {
      setLoading(false);
    }
  }, [form, user?.is_super_admin]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!user?.is_super_admin) {
    return (
      <div style={{ padding: 24 }}>
        <SmartBreadcrumbs />
        <Alert
          type="warning"
          showIcon
          message="Access restricted"
          description="This page is only available to super administrators."
        />
      </div>
    );
  }

  const onSave = async (vals: FormVals) => {
    setSaving(true);
    try {
      const payload: Partial<AiAgentConfig> = {
        enabled: vals.enabled,
        chartsEnabled: vals.chartsEnabled,
        exportEnabled: vals.exportEnabled,
        maxRows: vals.maxRows,
        allowedModules: vals.allowedModules || [],
        allowedTools: vals.allowedTools || [],
      };
      const res = await saveAiAgentConfig({ config: payload });
      if (!res.success) {
        message.error('Failed to save config');
        return;
      }
      message.success('AI assistant configuration saved');
      await load();
    } catch (e) {
      const m = e instanceof Error ? e.message : 'Save failed';
      message.error(m);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <SmartBreadcrumbs />
      <Title level={3} style={{ marginTop: 0 }}>
        AI assistant configuration
      </Title>
      <Text type="secondary">Company: {companyId || '—'}</Text>
      {ragMetrics?.metrics ? (
        <Card size="small" title="RAG cost / latency (7d)" style={{ marginTop: 12, maxWidth: 900 }}>
          <Space wrap>
            <Text>Chat interactions: {ragMetrics.metrics.interactions.count}</Text>
            <Text>Avg duration: {ragMetrics.metrics.interactions.avgDurationMs}ms</Text>
            <Text>P95 duration: {ragMetrics.metrics.interactions.p95DurationMs}ms</Text>
            <Text>Semantic p95: {ragMetrics.metrics.embedding.p95Ms}ms</Text>
            <Text>RC1 ranker: {ragMetrics.metrics.flags.rc1Ranker ? 'on' : 'off'}</Text>
            <Text>ERP indexer: {ragMetrics.metrics.flags.erpIndexer ? 'on' : 'off'}</Text>
          </Space>
        </Card>
      ) : null}
      {howTo ? (
        <Card size="small" title="How-to questions (30d)" style={{ marginTop: 12, maxWidth: 900 }}>
          <Space wrap style={{ marginBottom: 8 }}>
            <Text>Asked: {howTo.asked}</Text>
            <Text>Answered from a guide: {howTo.answeredFromGuide}</Text>
            {howTo.topGuides.length ? (
              <Text type="secondary">
                Top guides: {howTo.topGuides.slice(0, 5).map((g) => `${g.guideId} (${g.count})`).join(', ')}
              </Text>
            ) : null}
          </Space>
          <Table
            size="small"
            rowKey="question"
            pagination={{ pageSize: 10, hideOnSinglePage: true }}
            dataSource={howTo.unanswered}
            locale={{ emptyText: 'Every how-to question was answered from a guide.' }}
            columns={[
              { title: 'Question without a guide', dataIndex: 'question', key: 'question' },
              { title: 'Times', dataIndex: 'count', key: 'count', width: 70 },
              {
                title: 'Answered with',
                dataIndex: 'source',
                key: 'source',
                width: 140,
                render: (s: string) => (s === 'general' ? <Tag color="orange">General guidance</Tag> : <Tag>Nothing</Tag>),
              },
              {
                title: 'Last asked',
                dataIndex: 'lastAskedAt',
                key: 'lastAskedAt',
                width: 180,
                render: (v: string) => formatDateTime(v),
              },
            ]}
          />
        </Card>
      ) : null}
      <Card size="small" loading={loading} style={{ marginTop: 12, maxWidth: 900 }}>
        <Form<FormVals>
          form={form}
          layout="vertical"
          initialValues={{
            enabled: true,
            chartsEnabled: true,
            exportEnabled: true,
            maxRows: 200,
            allowedModules: [],
            allowedTools: [],
          }}
          onFinish={(vals) => void onSave(vals)}
        >
          <Space size="large" wrap>
            <Form.Item name="enabled" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Checkbox>Enable assistant</Checkbox>
            </Form.Item>
            <Form.Item name="chartsEnabled" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Checkbox>Enable charts in responses</Checkbox>
            </Form.Item>
            <Form.Item name="exportEnabled" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Checkbox>Enable export hints in responses</Checkbox>
            </Form.Item>
          </Space>

          <Form.Item
            name="maxRows"
            label="Maximum rows returned per list query"
            style={{ marginTop: 16 }}
            rules={[{ required: true, message: 'Max rows is required' }]}
          >
            <InputNumber min={20} max={500} step={10} style={{ width: 180 }} />
          </Form.Item>

          <Form.Item name="allowedModules" label="Allowed modules (empty = no module restriction)">
            <Select mode="multiple" options={MODULE_OPTIONS.map((m) => ({ value: m, label: m }))} />
          </Form.Item>

          <Form.Item name="allowedTools" label="Allowed tools (empty = no tool restriction)">
            <Select mode="multiple" options={TOOL_OPTIONS.map((t) => ({ value: t, label: t }))} />
          </Form.Item>

          <Space>
            <Button type="primary" htmlType="submit" loading={saving}>
              Save
            </Button>
            <Button onClick={() => void load()} disabled={saving}>
              Reload
            </Button>
          </Space>
        </Form>
      </Card>
    </div>
  );
};

export default AiAgentConfigPage;

